import { Injectable } from '@angular/core';
import { BaseDownloadServiceWithFilter } from './base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FuelReportFilter } from '@cl/shared/models/fuel-reports/fuel-report-filter.model';
import { NzImageService } from 'ng-zorro-antd/image';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { FuelReport } from '@cl/shared/models/fuel-reports/fuel-report.model';
import { CommandResult } from '@cl/shared/models';
import { DocumentFileInspection } from '@cl/shared/models/documents/document-file-inspection.model';
import { lastValueFrom, Subject } from 'rxjs';
import { FileMetadata } from '@cl/shared/models/file.model';
import { FuelReportApprovalAction } from '@cl/shared/models/fuel-reports/fuel-report-approval-action.enum';
import { ActionButton } from '@cl/shared/models/misc/action-button.model';
import { ESignFlowSelectComponent } from '@cl/shared/components';
import { ESignatureSignableFileType } from '@cl/shared/models/e-signature/e-signature-signable-file-type.enum';
import { StatusChangeDecisionNotesModalComponent } from '@cl/shared/components/status-change-decision-notes-modal/status-change-decision-notes-modal.component';

@Injectable({
    providedIn: 'root',
})
export class FuelReportsService extends BaseDownloadServiceWithFilter<FuelReport, FuelReportFilter> {
    availableChangeStatusActions: ActionButton[] = [
        {
            action: FuelReportApprovalAction.RETURN_TO_SUBMITTER,
            label: 'Atgriezt iesniedzējam',
            icon: 'undo',
            styleClasses: 'mr-2 btn-warning',
            type: 'info',
        },
        {
            action: FuelReportApprovalAction.SUBMIT,
            label: 'Iesniegt',
            icon: 'file-done',
            styleClasses: 'mr-2 btn-success',
            type: 'info',
        },
        {
            action: FuelReportApprovalAction.BOSS_APPROVAL,
            label: 'Saskaņot',
            icon: 'file-done',
            styleClasses: 'mr-2 btn-success',
            type: 'info',
        },
        {
            action: FuelReportApprovalAction.SIGN,
            label: 'Parakstīt',
            icon: 'signature',
            styleClasses: 'mr-2 btn-success',
            type: 'info',
        },
    ];

    reloadFuelReportSubject$: Subject<FuelReport> = new Subject<FuelReport>();

    constructor(
        protected httpClient: HttpClient,
        protected nzImageService: NzImageService,
        protected messageService: NzMessageService,
        protected modalService: NzModalService,
    ) {
        super(httpClient, '/api/v1/fuel-reports/', nzImageService);
    }

    async getFuelReportDetails(id: number): Promise<CommandResult<FuelReport>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<FuelReport>>(`${this.baseApiUrl}details/${id}`));
    }

    async getCarsLastFuelReport(carId: number): Promise<CommandResult<FuelReport>> {
        return await lastValueFrom(this.httpClient.get<CommandResult<FuelReport>>(`${this.baseApiUrl}cars-last-report/${carId}`));
    }

    async viewFuelReportPrintout(fuelReportId: number): Promise<CommandResult<FileMetadata>> {
        return this.handleFileDownloading(
            lastValueFrom(this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}${fuelReportId}/view-printout`)),
        );
    }

    async downloadFuelReportApprovalFile(fuelReportId: number, entryName?: string): Promise<CommandResult<FileMetadata>> {
        return this.handleFileDownloading(this.getFuelReportApprovalFileMetadata(fuelReportId, entryName));
    }

    async getFuelReportApprovalFileMetadata(fuelReportId: number, entryName?: string): Promise<CommandResult<FileMetadata>> {
        let params = new HttpParams();
        if (entryName !== undefined) {
            params = params.set('entryName', entryName);
        }

        return await lastValueFrom(
            this.httpClient.get<CommandResult<FileMetadata>>(`${this.baseApiUrl}${fuelReportId}/download-approval-file`, { params }),
        );
    }

    async inspectFuelReportApprovalFile(fuelReportId: number): Promise<CommandResult<DocumentFileInspection>> {
        return await lastValueFrom(
            this.httpClient.get<CommandResult<DocumentFileInspection>>(`${this.baseApiUrl}${fuelReportId}/inspect-approval-file`),
        );
    }

    async changeStatus(action: string, fuelReport: FuelReport): Promise<boolean> {
        return await new Promise<boolean>(async (resolve, reject) => {
            try {
                switch (action) {
                    case FuelReportApprovalAction.SIGN:
                        await this.initializeESigning(fuelReport);
                        resolve(false);
                        return;

                    case FuelReportApprovalAction.SUBMIT:
                    case FuelReportApprovalAction.BOSS_APPROVAL:
                        let changeStatusParams: HttpParams = new HttpParams()
                            .append('fuelReportId', fuelReport.id.toString())
                            .append('action', action);
                        await this.changeStatusRequest(changeStatusParams);
                        fuelReport = (await this.getFuelReportDetails(fuelReport.id)).data;
                        this.reloadFuelReportSubject$.next(fuelReport);
                        await this.initializeESigning(fuelReport);
                        resolve(false);
                        return;

                    case FuelReportApprovalAction.RETURN_TO_SUBMITTER:
                    case FuelReportApprovalAction.DECLINE:
                        const decisionNoteNameLv: string =
                            action == FuelReportApprovalAction.RETURN_TO_SUBMITTER ? 'Atgriešanas iemesls' : 'Noraidīšanas iemesls';
                        const decisionNotesModalRef: NzModalRef<StatusChangeDecisionNotesModalComponent> = this.modalService.create({
                            nzTitle: decisionNoteNameLv,
                            nzContent: StatusChangeDecisionNotesModalComponent,
                        });

                        decisionNotesModalRef.afterOpen.subscribe((): void => {
                            decisionNotesModalRef.getContentComponent().decisionNoteNameLv = decisionNoteNameLv;
                            decisionNotesModalRef.getContentComponent().decisionNotes = fuelReport.decisionNotes;
                        });

                        decisionNotesModalRef.afterClose.subscribe(async (decisionNotes: string): Promise<void> => {
                            if (!decisionNotes) {
                                resolve(false);
                                return;
                            }

                            try {
                                let changeStatusRequestParams: HttpParams = new HttpParams()
                                    .append('fuelReportId', fuelReport.id)
                                    .append('action', action)
                                    .append('decisionNotes', decisionNotes);

                                await this.changeStatusRequest(changeStatusRequestParams);
                                resolve(true);
                            } catch (ex) {
                                console.error(ex);
                                if (ex.error?.errors) {
                                    ex.error.errors.forEach((error) => {
                                        this.messageService.error(error);
                                    });
                                    reject(ex);
                                    return;
                                }

                                this.messageService.error(`Kļūda mainot degvielas atskaites statusu`);
                                reject(ex);
                            }
                        });
                        return;
                }
            } catch (ex) {
                console.error(ex);
                if (ex.error?.errors) {
                    ex.error.errors.forEach((error) => {
                        this.messageService.error(error);
                    });
                    reject(ex);
                    return;
                }

                this.messageService.error(`Kļūda mainot degvielas atskaites statusu`);
                reject(ex);
            }
        });
    }

    toString(object: FuelReport): string {
        return object.id.toString();
    }

    private async changeStatusRequest(params: HttpParams): Promise<void> {
        return await lastValueFrom(this.httpClient.patch<void>(`${this.baseApiUrl}change-status`, null, { params: params }));
    }

    private async initializeESigning(fuelReport: FuelReport): Promise<void> {
        return new Promise<void>((resolve) => {
            if (fuelReport.reportApprovalFile == null || !fuelReport.availableActions.includes(FuelReportApprovalAction.SIGN)) {
                return;
            }

            const modal = this.modalService.create({
                nzTitle: 'Autentifikācijas veida izvēle',
                nzContent: ESignFlowSelectComponent,
                nzWidth: 'fit-content',
                nzFooter: null,
            });

            modal.afterOpen.subscribe((): void => {
                modal.getContentComponent().fileIds = [fuelReport.reportApprovalFile.id];
                modal.getContentComponent().signableFileType = ESignatureSignableFileType.FUEL_REPORT_APPROVAL;
                modal.getContentComponent().fuelReportId = fuelReport.id;
            });

            modal.afterClose.subscribe((): void => {
                resolve();
            });
        });
    }
}
